import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiJson, apiSend } from "@/api/client";
import { operationKeys, type EventKind } from "@/api/operations";
import { segment, withQuery } from "@/api/query";
import { reportKeys } from "@/api/reports";
import type { VesselCallInput } from "@/api/vessels";

/* Mirrors api/src/pbo_invoicing/mail/schemas.py. */

export type SuggestionKind =
  "new_vessel" | "new_call" | "call_event" | "quotation_request" | "link";
export type SuggestionStatus = "pending" | "accepted" | "dismissed" | "auto";
export type MessageStatus = "new" | "filed" | "ignored";

export type MailStatus = {
  configured: boolean;
  mailbox: string | null;
  last_polled_at: string | null;
  last_error: string;
  pending: number;
  messages_seen: number;
};

export type MessageBrief = {
  id: string;
  from_addr: string;
  from_name: string;
  subject: string;
  sent_at: string;
  status: MessageStatus;
  is_ours: boolean;
  vessel_call_id: string | null;
  attachment_count: number;
};

export type Attachment = {
  id: string;
  filename: string;
  mime_type: string;
  byte_len: number;
  stored: boolean;
};

/** What the matcher found for a `new_call` suggestion, as the server stores it. */
export type NewCallPayload = {
  vessel_id: string;
  vessel_name: string;
  client_id: string | null;
  terminal: string;
  port: string;
  eta: string | null;
  voyage: string;
};

export type Suggestion = {
  id: string;
  message_id: string;
  kind: SuggestionKind;
  confidence: "high" | "medium" | "low";
  payload: Record<string, unknown>;
  status: SuggestionStatus;
  decided_by_username: string | null;
  decided_at: string | null;
  reason: string;
  result: Record<string, unknown> | null;
  created_at: string;
  message: MessageBrief;
};

export type Message = MessageBrief & {
  to_addrs: string[];
  cc_addrs: string[];
  text_body: string;
  client_id: string | null;
  vessel_id: string | null;
  filed_by_username: string | null;
  filed_at: string | null;
  attachments: Attachment[];
  suggestions: Suggestion[];
};

export type AcceptEventInput = {
  kind: EventKind;
  happened_at: string;
  note: string;
};
export type AcceptNewVesselInput = { name: string; imo: string };
export type AcceptNewCallInput = VesselCallInput & {
  invoice_date: string | null;
};
export type AcceptInput =
  AcceptEventInput | AcceptNewVesselInput | AcceptNewCallInput | Record<string, never>;

export type MessageFilters = {
  callId?: string;
  vesselId?: string;
  status?: MessageStatus;
  q?: string;
};

export const mailKeys = {
  all: ["mail"] as const,
  status: ["mail", "status"] as const,
  suggestions: (status: SuggestionStatus) => ["mail", "suggestions", status] as const,
  messages: (filters: MessageFilters) => ["mail", "messages", filters] as const,
  message: (id: string) => ["mail", "message", id] as const,
};

// --------------------------------------------------------------------------- requests

export function getMailStatus(): Promise<MailStatus> {
  return apiJson<MailStatus>("/mail/status");
}

export function listSuggestions(status: SuggestionStatus): Promise<Suggestion[]> {
  return apiJson<Suggestion[]>(withQuery("/mail/suggestions", { status }));
}

export function acceptSuggestion(id: string, input: AcceptInput): Promise<Suggestion> {
  return apiSend<Suggestion>(`/mail/suggestions/${segment(id)}/accept`, "POST", input);
}

export function dismissSuggestion(id: string, reason: string): Promise<Suggestion> {
  return apiSend<Suggestion>(`/mail/suggestions/${segment(id)}/dismiss`, "POST", { reason });
}

export function listMessages(filters: MessageFilters): Promise<MessageBrief[]> {
  return apiJson<MessageBrief[]>(
    withQuery("/mail/messages", {
      call_id: filters.callId,
      vessel_id: filters.vesselId,
      status: filters.status,
      q: filters.q,
      limit: 100,
    }),
  );
}

export function getMessage(id: string): Promise<Message> {
  return apiJson<Message>(`/mail/messages/${segment(id)}`);
}

export function fileMessage(id: string, vesselCallId: string): Promise<Message> {
  return apiSend<Message>(`/mail/messages/${segment(id)}/file`, "POST", {
    vessel_call_id: vesselCallId,
  });
}

export function ignoreMessage(id: string): Promise<Message> {
  return apiSend<Message>(`/mail/messages/${segment(id)}/ignore`, "POST");
}

export function eventFromMessage(
  id: string,
  input: AcceptEventInput & { vessel_call_id?: string },
): Promise<Message> {
  return apiSend<Message>(`/mail/messages/${segment(id)}/event`, "POST", input);
}

// --------------------------------------------------------------------------- hooks

export function useMailStatus() {
  return useQuery({
    queryKey: mailKeys.status,
    queryFn: getMailStatus,
    staleTime: 60_000,
  });
}

export function useSuggestions(status: SuggestionStatus = "pending") {
  return useQuery({
    queryKey: mailKeys.suggestions(status),
    queryFn: () => listSuggestions(status),
  });
}

export function useMessages(filters: MessageFilters) {
  return useQuery({
    queryKey: mailKeys.messages(filters),
    queryFn: () => listMessages(filters),
  });
}

export function useMessage(id: string | null) {
  return useQuery({
    queryKey: mailKeys.message(id ?? ""),
    queryFn: () => getMessage(id ?? ""),
    enabled: id !== null,
  });
}

/** Any decision on mail can create calls, events and drafts: refresh everything that shows them. */
function useMailMutation<Variables, Result>(mutationFn: (variables: Variables) => Promise<Result>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () =>
      Promise.all(
        [mailKeys.all, operationKeys.all, reportKeys.all, ["invoices"], ["vessels"]].map(
          (queryKey) => queryClient.invalidateQueries({ queryKey }),
        ),
      ),
  });
}

export function useAcceptSuggestion() {
  return useMailMutation(({ id, input }: { id: string; input: AcceptInput }) =>
    acceptSuggestion(id, input),
  );
}

export function useDismissSuggestion() {
  return useMailMutation(({ id, reason }: { id: string; reason: string }) =>
    dismissSuggestion(id, reason),
  );
}

export function useFileMessage() {
  return useMailMutation(({ id, vesselCallId }: { id: string; vesselCallId: string }) =>
    fileMessage(id, vesselCallId),
  );
}

export function useIgnoreMessage() {
  return useMailMutation((id: string) => ignoreMessage(id));
}

export function useEventFromMessage() {
  return useMailMutation(
    ({ id, input }: { id: string; input: AcceptEventInput & { vessel_call_id?: string } }) =>
      eventFromMessage(id, input),
  );
}
